import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Upload, Loader, Sparkles } from 'lucide-react';
import {
  getUsdaMeta,
  importUsdaCsvs,
  previewUsdaBulkMatch,
  runUsdaBulkMatch,
} from '../../../utils/unitConversionApi';

export function UnitConversionPage() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState<any>(null);
  const [foodFile, setFoodFile] = useState<File | null>(null);
  const [portionsFile, setPortionsFile] = useState<File | null>(null);
  const [importReport, setImportReport] = useState<Record<string, unknown> | null>(null);
  const [bulkLang, setBulkLang] = useState('fr');
  const [pageSize, setPageSize] = useState(25);
  const [offset, setOffset] = useState(0);
  const [bulkPreview, setBulkPreview] = useState<Record<string, unknown> | null>(null);
  const [bulkReport, setBulkReport] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refreshMeta = async () => {
    try {
      const res = await getUsdaMeta();
      setMeta(res.meta);
    } catch (err) {
      console.warn(err);
    }
  };

  useEffect(() => {
    refreshMeta();
  }, []);

  const withBusy = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const reviewBase = '/admin/mass-db/conversion/match-review';

  return (
    <div className="space-y-8">
      <p className="text-sm text-gray-500">
        USDA pièce → g : import CSV, puis matching paginé (Gemini traduit à la volée, sans
        stocker le nom EN). Densités volume → saisie manuelle sur l&apos;ingrédient.
      </p>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        <p>
          Portions USDA en base :{' '}
          <span className="font-medium text-menioo-navy">
            {meta?.currentCount ?? '— aucune importée'}
          </span>
        </p>
        {meta?.lastImport && (
          <p className="mt-1">
            Dernier import : {String(meta.lastImport.portionsKept ?? meta.lastImport.written)}{' '}
            portions pièce
            {meta.lastImport.skippedVolume != null
              ? ` (${meta.lastImport.skippedVolume} volumes exclus)`
              : ''}
          </p>
        )}
      </div>

      <section className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-display font-bold text-menioo-navy">1. Import USDA</h3>
        <p className="text-sm text-gray-500">
          Uploadez <code>food.csv</code> et <code>food_portion.csv</code>. Seules les portions
          « pièce » sont conservées.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block text-sm">
            <span className="font-medium text-menioo-navy">food.csv</span>
            <input
              type="file"
              accept=".csv"
              className="mt-1 block w-full text-sm"
              onChange={(e) => setFoodFile(e.target.files?.[0] || null)}
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-menioo-navy">food_portion.csv</span>
            <input
              type="file"
              accept=".csv"
              className="mt-1 block w-full text-sm"
              onChange={(e) => setPortionsFile(e.target.files?.[0] || null)}
            />
          </label>
        </div>
        <button
          type="button"
          disabled={!foodFile || !portionsFile || !!busy}
          onClick={() =>
            withBusy('import', async () => {
              const res = await importUsdaCsvs(foodFile!, portionsFile!);
              setImportReport(res.report);
              await refreshMeta();
            })
          }
          className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-menioo-green hover:bg-menioo-green-dark disabled:opacity-50"
        >
          {busy === 'import' ? (
            <Loader className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Upload className="h-4 w-4 mr-2" />
          )}
          Importer
        </button>
        {importReport && (
          <pre className="text-xs bg-gray-50 p-3 rounded-md overflow-auto">
            {JSON.stringify(importReport, null, 2)}
          </pre>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-display font-bold text-menioo-navy">
          2. Matching pièce (paginé)
        </h3>
        <p className="text-sm text-gray-500">
          Pour chaque page : Gemini traduit le nom FR → EN (éphémère), recherche USDA, file de
          review. Aucun auto-apply. Coût Gemini ≈ 1 appel × taille de page.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            Langue
            <select
              value={bulkLang}
              onChange={(e) => setBulkLang(e.target.value)}
              className="mt-1 block rounded-md border-gray-300 text-sm"
            >
              <option value="fr">FR</option>
              <option value="en">EN</option>
              <option value="all">Toutes</option>
            </select>
          </label>
          <label className="text-sm">
            Page size
            <input
              type="number"
              min={1}
              max={100}
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value) || 25)}
              className="mt-1 block w-24 rounded-md border-gray-300 text-sm"
            />
          </label>
          <label className="text-sm">
            Offset
            <input
              type="number"
              min={0}
              value={offset}
              onChange={(e) => setOffset(Number(e.target.value) || 0)}
              className="mt-1 block w-24 rounded-md border-gray-300 text-sm"
            />
          </label>
          <button
            type="button"
            disabled={!!busy}
            onClick={() =>
              withBusy('preview', async () => {
                const res = await previewUsdaBulkMatch({
                  language: bulkLang,
                  limit: pageSize,
                  offset,
                });
                setBulkPreview(res.preview);
              })
            }
            className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {busy === 'preview' ? <Loader className="h-4 w-4 mr-2 animate-spin" /> : null}
            Preview (gratuit)
          </button>
          <button
            type="button"
            disabled={!!busy}
            onClick={() =>
              withBusy('run', async () => {
                const res = await runUsdaBulkMatch({
                  language: bulkLang,
                  limit: pageSize,
                  offset,
                });
                setBulkReport(res.report);
                if (res.report.nextOffset != null) {
                  setOffset(Number(res.report.nextOffset));
                }
                navigate(`${reviewBase}?runId=${res.report.runId}`);
              })
            }
            className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-menioo-green hover:bg-menioo-green-dark disabled:opacity-50"
          >
            {busy === 'run' ? (
              <Loader className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            Traiter {pageSize} + review
          </button>
          <Link to={reviewBase} className="text-sm text-menioo-green hover:underline">
            Dernière review →
          </Link>
        </div>
        {bulkPreview && (
          <div className="space-y-2">
            <p className="text-sm text-menioo-navy">
              Éligibles : {String(bulkPreview.eligibleTotal)} · page{' '}
              {Math.floor(Number(bulkPreview.offset) / Number(bulkPreview.pageSize || 25)) + 1}/
              {String(bulkPreview.pageCount || 0)} · nextOffset:{' '}
              {String(bulkPreview.nextOffset ?? '—')}
            </p>
            <pre className="text-xs bg-gray-50 p-3 rounded-md overflow-auto max-h-48">
              {JSON.stringify(bulkPreview, null, 2)}
            </pre>
          </div>
        )}
        {bulkReport && (
          <pre className="text-xs bg-gray-50 p-3 rounded-md overflow-auto">
            {JSON.stringify(bulkReport, null, 2)}
          </pre>
        )}
      </section>
    </div>
  );
}
